/**
 * Core-100 R4 — CORE-Y: which Create Ticket fields a machine proposed, with what confidence, and which it could not
 * find at all — kept from the moment an extraction prefills the form until the person submits it.
 *
 * Constitution (CORE-Y §Constitutional Responsibilities):
 *   "Extracted fields SHALL be distinguishable from entered fields at the point of review, and where a field could
 *    not be extracted, that MUST be shown as absent rather than blank."
 *   "Extraction confidence SHALL be surfaced where it exists, and low confidence MUST NOT be presented identically to
 *    high confidence."
 *   §Provenance: "After commitment, the origination method should remain recoverable" — `creationMethodFor`.
 *
 * Before this module the repair-order path showed a transient toast listing LOW fields and nothing on the form, and
 * the conversation path said "Review the highlighted fields" while highlighting nothing.
 *
 * Pure: no React, no I/O.
 */

export type ExtractionOrigin = 'document_upload' | 'conversation';
export type ExtractedConfidence = 'high' | 'medium' | 'low' | 'unknown';

/** The Create Ticket form fields an extraction can propose (react-hook-form paths). */
export const PROVENANCE_FIELDS = [
  'customerName',
  'customerPhone',
  'vehicle.vin',
  'vehicle.licensePlate',
  'vehicle.year',
  'vehicle.make',
  'vehicle.model',
  'vehicle.engineSize',
  'vehicle.odometerMiles',
] as const;
export type ProvenanceField = (typeof PROVENANCE_FIELDS)[number];

export interface FieldProvenanceEntry {
  /** 'extracted': the machine proposed `value`; 'not_found': it looked and found nothing. */
  state: 'extracted' | 'not_found';
  confidence: ExtractedConfidence | null;
  /** What the machine proposed (normalised to a string), so a later human edit is recognisable. */
  value: string | null;
}

export interface ExtractionProvenance {
  origin: ExtractionOrigin;
  fields: Partial<Record<ProvenanceField, FieldProvenanceEntry>>;
}

const norm = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
};

const tier = (c: unknown): ExtractedConfidence => {
  if (c === 'high' || c === 'medium' || c === 'low') return c;
  return 'unknown';
};

function entry(value: unknown, confidence: ExtractedConfidence | null): FieldProvenanceEntry {
  const v = norm(value);
  return v === null
    ? { state: 'not_found', confidence: null, value: null }
    : { state: 'extracted', confidence: confidence ?? 'unknown', value: v };
}

/** parse-ro's result → provenance. `field_confidence` is per field ('none' = not found). */
export function roExtractionProvenance(data: {
  vin?: unknown; year?: unknown; make?: unknown; model?: unknown; engineSize?: unknown;
  licensePlate?: unknown; mileage?: unknown; odometerMiles?: unknown; customerName?: unknown; customerPhone?: unknown;
  field_confidence?: Record<string, string> | null;
}): ExtractionProvenance {
  const fc = data.field_confidence ?? {};
  // A field the parser scored but reports no confidence for is UNKNOWN, never high.
  const c = (k: string) => (k in fc ? tier(fc[k]) : 'unknown');
  return {
    origin: 'document_upload',
    fields: {
      customerName: entry(data.customerName, c('customerName')),
      customerPhone: entry(data.customerPhone, c('customerPhone')),
      'vehicle.vin': entry(data.vin, c('vin')),
      'vehicle.licensePlate': entry(data.licensePlate, c('licensePlate')),
      'vehicle.year': entry(data.year, c('year')),
      'vehicle.make': entry(data.make, c('make')),
      'vehicle.model': entry(data.model, c('model')),
      // the engine is inferred from the decode / catalogue, not scored by the parser
      'vehicle.engineSize': entry(data.engineSize, 'unknown'),
      'vehicle.odometerMiles': entry(data.odometerMiles ?? data.mileage, c('mileage')),
    },
  };
}

/** A conversation extraction carries ONE overall confidence (0..1); every proposed field inherits it. */
export function conversationConfidenceTier(confidence: number | null | undefined): ExtractedConfidence {
  if (typeof confidence !== 'number' || !Number.isFinite(confidence)) return 'unknown';
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

export function conversationExtractionProvenance(
  prefill: {
    customerName?: unknown; customerPhone?: unknown; vin?: unknown; licensePlate?: unknown;
    year?: unknown; make?: unknown; model?: unknown; odometerMiles?: unknown;
  },
  overallConfidence: number | null | undefined,
): ExtractionProvenance {
  const t = conversationConfidenceTier(overallConfidence);
  return {
    origin: 'conversation',
    fields: {
      customerName: entry(prefill.customerName, t),
      customerPhone: entry(prefill.customerPhone, t),
      'vehicle.vin': entry(prefill.vin, t),
      'vehicle.licensePlate': entry(prefill.licensePlate, t),
      'vehicle.year': entry(prefill.year, t),
      'vehicle.make': entry(prefill.make, t),
      'vehicle.model': entry(prefill.model, t),
      'vehicle.odometerMiles': entry(prefill.odometerMiles, t),
    },
  };
}

export type FieldMark =
  | { kind: 'extracted'; origin: ExtractionOrigin; confidence: ExtractedConfidence }
  | { kind: 'not_found'; origin: ExtractionOrigin }
  | { kind: 'edited'; origin: ExtractionOrigin };

const same = (a: string | null, b: string | null) =>
  a !== null && b !== null && a.toUpperCase() === b.toUpperCase();

/**
 * How a field should read at review, given what the extraction proposed and what the form holds NOW.
 *   extracted   the form still holds the machine's value — shown with its confidence
 *   edited      the machine's value was changed afterwards (by the person, or by a VIN decode) — no longer the
 *               extraction's proposal, and said so
 *   not_found   the machine looked and found nothing, and the field is still empty — absent, not blank
 *   null        no extraction touched this field, or a person filled a field the machine could not find
 */
export function fieldMark(
  provenance: ExtractionProvenance | null | undefined,
  field: ProvenanceField,
  currentValue: unknown,
): FieldMark | null {
  const e = provenance?.fields[field];
  if (!provenance || !e) return null;
  const now = norm(currentValue);
  if (e.state === 'not_found') return now === null ? { kind: 'not_found', origin: provenance.origin } : null;
  if (same(now, e.value)) return { kind: 'extracted', origin: provenance.origin, confidence: e.confidence ?? 'unknown' };
  return { kind: 'edited', origin: provenance.origin };
}

const ORIGIN_WORDS: Record<ExtractionOrigin, string> = {
  document_upload: 'the repair order',
  conversation: 'the conversation',
};
const CONFIDENCE_WORDS: Record<ExtractedConfidence, string> = {
  high: 'high confidence',
  medium: 'medium confidence — check it',
  low: 'LOW confidence — verify before submitting',
  unknown: 'confidence unknown — verify before submitting',
};

export function fieldMarkText(mark: FieldMark): string {
  switch (mark.kind) {
    case 'extracted': return `From ${ORIGIN_WORDS[mark.origin]} · ${CONFIDENCE_WORDS[mark.confidence]}`;
    case 'not_found': return `Not found in ${ORIGIN_WORDS[mark.origin]}`;
    case 'edited': return `Changed after extraction from ${ORIGIN_WORDS[mark.origin]}`;
  }
}

/** The origination method a submitted Create Ticket records (tickets.creation_method, set once). */
export function creationMethodFor(
  provenance: ExtractionProvenance | null | undefined,
): 'create_ticket' | ExtractionOrigin {
  return provenance?.origin ?? 'create_ticket';
}
